import { useMemo } from 'react'
import { useConnectedPlatforms } from '../../autopilot/hooks/useConnectedPlatforms'
import type { LivePlatform } from '../store/idea-store'

export interface PlatformConnectionState {
  /** True when the platform requires no OAuth or the user has a live token. */
  connected: boolean
  /** False for platforms that have no OAuth provider. */
  needsConnection: boolean
  /** OAuth provider key to pass to openOAuthPopup. Null for platforms without OAuth. */
  oauthProvider: string | null
}

export interface UsePlatformConnectionsResult {
  loading: boolean
  connections: Record<LivePlatform, PlatformConnectionState>
  refetch: () => Promise<void>
}

/**
 * Maps each LivePlatform to its OAuth provider key. Most platforms map to
 * themselves; YouTube Shorts is published via the same OAuth as YouTube
 * (server-side providerId is 'youtube'), and X is published via the
 * `twitter` integration row. Without the X entry `connections.x` was
 * undefined and PostModal's `?? true` fallback claimed X was connected for
 * every business, so the connect-on-submit flow never ran for it.
 */
const PLATFORM_TO_OAUTH_PROVIDER: Record<LivePlatform, string | null> = {
  instagram: 'instagram',
  facebook: 'facebook',
  linkedin: 'linkedin',
  tiktok: 'tiktok',
  youtube_shorts: 'youtube',
  pinterest: 'pinterest',
  x: 'twitter',
}

export const ALL_PLATFORMS = Object.keys(PLATFORM_TO_OAUTH_PROVIDER) as LivePlatform[]

/**
 * Per-LivePlatform connection state for the PostModal. Wraps
 * useConnectedPlatforms with the LivePlatform→OAuth-provider mapping so the
 * modal can lookup `connections[platform].connected` directly.
 */
export function usePlatformConnections(): UsePlatformConnectionsResult {
  const { loading, connectedProviders, refetch } = useConnectedPlatforms()

  const connections = useMemo(() => {
    const connectedSet = new Set(connectedProviders)
    return ALL_PLATFORMS.reduce(
      (acc, platform) => {
        const oauthProvider = PLATFORM_TO_OAUTH_PROVIDER[platform]
        const needsConnection = oauthProvider !== null
        acc[platform] = {
          oauthProvider,
          needsConnection,
          connected: needsConnection ? connectedSet.has(oauthProvider as string) : true,
        }
        return acc
      },
      {} as Record<LivePlatform, PlatformConnectionState>
    )
  }, [connectedProviders])

  return { loading, connections, refetch }
}
